import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Location, ViewportScroller } from '@angular/common';
import { Router, Scroll } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, merge, Subject, takeUntil, tap } from 'rxjs';
import { CalibrationStatus, ParticleCalibration, ParticleCalibrationFilter } from 'src/app/model/particleCalibration';
import { ParticleCalibrationDataSource } from 'src/app/model/particleCalibrationDataSource';
import { User } from 'src/app/model/user';
import { ParticleCalibrationService } from 'src/app/services/particleCalibration.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { ThemeService } from 'src/app/services/theme.service';
import { LangFlag } from 'src/app/services/translation.service';
import { UserService } from 'src/app/services/user.service';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-particles-list',
  templateUrl: './particles-list.component.html',
  standalone: false,
})
export class ParticlesListComponent implements OnInit, OnDestroy, AfterViewInit{

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  currentUser: User;
  
  dataSource: ParticleCalibrationDataSource;
  filter: ParticleCalibrationFilter = new ParticleCalibrationFilter();

  public checkedItems = new Map<ParticleCalibration, boolean>();

  displayedColumns: string[] = [
    'checkbox', 'projectNo', 'certificateNum', 'client', 'name', 'equipment', 'serialNum', 'status', 'calibrateDate', 'expirationDate',
    'realizationDate', 'download', 'edit', 'delete', 'clone', 'more'
  ];

  hasFiltered = false;
    langs: LangFlag[];
  
    private readonly destroy$ = new Subject<void>();

     constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly particleCalibrationService: ParticleCalibrationService,
    public dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly themeService: ThemeService,
    private readonly userService: UserService,
    public snackBarService: SnackBarService,
    private readonly spinnerService: SpinnerService,
    private readonly viewportScroller: ViewportScroller) {
    this.router.events.pipe(filter(e => e instanceof Scroll)).pipe(takeUntil(this.destroy$)).subscribe((e: Scroll) => {
      setTimeout(() => {
        if (e.position) {
          this.viewportScroller.scrollToPosition(e.position);
        } else if (e.anchor) {
          this.viewportScroller.scrollToAnchor(e.anchor);
        } else {
          this.viewportScroller.scrollToPosition([0, 0]);
        }
      });
    });
  }

  get pageTitle(): string {
      return this.translate.instant('calibrateEquipmentList.title') as string;
    }
  
    ngOnInit(): void {
      this.currentUser = this.userService.currentProfile;
  
      const excludedCols: string[] = [];
  
      if (!this.currentUser.showClone) {
        excludedCols.push('clone');
      }
  
      this.displayedColumns = this.displayedColumns.filter(c => !excludedCols.includes(c));
  
      const filterResult = localStorage.getItem('filter');
      if (filterResult) {
        this.hasFiltered = true;
        this.filter = JSON.parse(filterResult) as ParticleCalibrationFilter;
      }
      this.loadCalibrationList();
    }

    loadCalibrationList(): void {
        this.checkedItems = new Map<ParticleCalibration, boolean>();
    
        this.dataSource = new ParticleCalibrationDataSource(this.particleCalibrationService);
    
        this.filter.sortBy = this.sort.active || 'calibrateDate';
        this.filter.sortDirection = this.sort.direction || 'desc';
        this.filter.pageIndex = this.paginator.pageIndex || 0;
        this.filter.pageSize = this.paginator.pageSize || 20;
    
        if (this.hasFiltered) {
          this.saveSearchFilter(this.filter);
          this.dataSource.loadCalibration(this.filter);
        }
        this.dataSource.loading$.pipe(takeUntil(this.destroy$)).subscribe(message => this.spinnerService.next(message));
      }

      saveSearchFilter(filter: ParticleCalibrationFilter): void {
        localStorage.setItem('filter', JSON.stringify(filter));
      }

    getThemeIconPath(): string {
      const url = this.router.url;
      return this.themeService.getThemeIconPathSelection(url);
    }

    ngAfterViewInit(): void {
      // reset the paginator after sorting
      this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.paginator.pageIndex = 0);
  
      merge(this.sort.sortChange, this.paginator.page).pipe(tap(() => this.loadCalibrationList())).pipe(takeUntil(this.destroy$)).subscribe();
    }

    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }

    exportTable(): void {
      this.filter.cols = this.displayedColumns;
  
      this.spinnerService.show();
     /*this.particleCalibrationService.getExcel(this.filter).pipe(takeUntil(this.destroy$)).subscribe((res: Blob) => {
        const name = this.pageTitle + '.xlsx';
        saveAs(res, name);
        this.spinnerService.hide();
      }, error => {
        console.error(error);
        this.spinnerService.hide();
      });*/
    }

    doFilter(event: ParticleCalibrationFilter): void {
      this.filter = event;
  
      this.hasFiltered = true;
  
      this.paginator.pageIndex = 0;
      this.loadCalibrationList();
    }

    cancel(): void {
      this.location.back();
    }

  showAutomaticSign(): boolean {
    const current = this.currentUser;

    return current.automaticSign;
  }

  new(): void {
    let url: string;
    if (this.router.url.includes('particles')){
      url = 'particles/calibration?id=0'
    }
    void this.router.navigateByUrl(url);

  }

  showSelectAll(): boolean {
    return this.dataSource.data.length > 0;
  }

  isAllSelected(): boolean {
    return this.dataSource.data.every(item => this.isSelected(item));
  }

  isSelected(item: ParticleCalibration): boolean {
    if (item == null) {
      return false;
    }

    return this.checkedItems.get(item);
  }

  showCheckbox(calibration: ParticleCalibration): boolean {
    const allowedStatuses = [CalibrationStatus.EN_EJECUCION, CalibrationStatus.PENDIENTE_FIRMA, CalibrationStatus.FIRMADO];

    return allowedStatuses.includes(calibration.idStatus);
  }

  highlight(calibration: ParticleCalibration): string {
      return this.isCanceled(calibration) ? 'deactivate' : 'action';
    }

  private isCanceled(calibration: ParticleCalibration): boolean {
    return calibration.idStatus === CalibrationStatus.NO_VALIDO;
  }

  canDownload(calibration: ParticleCalibration): boolean {
    const allowedStatusesDownload = [CalibrationStatus.PENDIENTE_FIRMA, CalibrationStatus.FIRMADO];

    return allowedStatusesDownload.includes(calibration.idStatus);
  }

  editRow(id: number): void {
    let url: string;
    url = `particles/calibration?id=${id}`
    void this.router.navigateByUrl(url);
  }

  canDelete(calibration: ParticleCalibration): boolean {
    const allowedStatuses = [CalibrationStatus.EN_EJECUCION];

    return allowedStatuses.includes(calibration.idStatus) && +calibration.signedVersion === 0;
  }

  canClone(calibration: ParticleCalibration): boolean {
    return calibration?.idStatus === CalibrationStatus.FIRMADO;
  }
}
